// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  addUrinalysisTest,
  deleteUrinalysisTest,
  updateUrinalysisTest,
} from '../../../actions/testingActions';

import { Error } from '../../Typography.jsx';
import UrinalysisForm, { normalValues } from './UrinalysisForm.jsx';
import SimpleTable from '../../SimpleTable.jsx';
import Modal from '../../Modal.jsx';
import { Button, PrimaryButton, DeleteButton } from '../../Buttons.jsx';

type Props = {
  urinalysisTests: Object[],
  activePatientId: string,
  activeAccount: Object,
  addUrinalysisTest: Function,
  deleteUrinalysisTest: Function,
  updateUrinalysisTest: Function,
  print: boolean,
}

type State = {
  modalOpen: bool,
  viewOnly: bool,
  editRow: ?Object,
}

class UrinalysisTestsComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      viewOnly: false,
      editRow: null,
    };
  }

  // Modal state handlers. editRow is set to row object if editing, else null.
  handleEditRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: false,
      editRow: row,
    });
  }

  handleViewRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: true,
      editRow: row,
    });
  }

  handleAddRow = () => {
    this.setState({
      modalOpen: true,
      viewOnly: false,
      editRow: null,
    });
  }

  closeModal = () => {
    this.setState({
      modalOpen: false,
      editRow: null,
    });
  }

  // Misc functions
  /* eslint-disable no-param-reassign */
  transformData(formValues: Object) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY').toISOString();
    // add active account for edit history
    const { activeAccount } = this.props;
    formValues.editor = `${activeAccount.name}, ${activeAccount.credential}`;
    formValues.editDate = moment().toISOString();
  }

  // Endpoint action handlers
  handleDeleteRow = (id: string) => {
    this.props.deleteUrinalysisTest(this.props.activePatientId, id);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformData(formData.values);
    this.props.updateUrinalysisTest(this.props.activePatientId, {
      testId: id,
      ...formData.values,
    });
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformData(formData.values);
    this.props.addUrinalysisTest(this.props.activePatientId, formData.values);
  }

  // Create action column for each row
  actionsAccessor = (row: Object) => {
    const defaultButtons = (
      <div>
        <Button onClick={() => this.handleViewRow(row)}>View</Button>
        <PrimaryButton onClick={() => this.handleEditRow(row)}>Edit</PrimaryButton>
        <DeleteButton onClick={() => this.handleDeleteRow(row._id)}>X</DeleteButton>
      </div>
    );
    return defaultButtons;
  }

  render() {
    let { urinalysisTests } = this.props;
    urinalysisTests = urinalysisTests.map((item, index) => ({ testNum: urinalysisTests.length - index, ...item }));

    const columns = [
      {
        Header: '',
        id: 'index',
        accessor: item => `Test #${item.testNum ? item.testNum : ''}`,
        width: 110,
      },
      {
        Header: 'Date',
        id: 'date',
        accessor: item => (item.date ? moment(item.date).format() : '--'),
      },
      {
        Header: 'Type',
        id: 'type',
        accessor: () => 'Urine',
      },
      {
        Header: 'Result',
        id: 'result',
        // Logic for table result we show: normal if all fields filled out with normal value,
        // abnormal if any abnormal value, '--' if filled out fields are all normal,
        // but not all fields are filled out.
        accessor: (item) => {
          let missingValue = false;
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of Object.entries(normalValues)) {
            if (!item[key]) {
              missingValue = true;
              // eslint-disable-next-line no-continue
              continue;
            }
            if (item[key] !== value) {
              return <Error>Abnormal</Error>;
            }
          }
          return missingValue ? '--' : 'Normal';
        },
      },
    ];

    if (!this.props.print) {
      columns.push({
        Header: 'Actions',
        id: 'actions',
        maxWidth: 120,
        accessor: this.actionsAccessor,
      });
    }

    if (this.props.print && !urinalysisTests.length) {
      return 'No data';
    }

    return (
      <div>
        {!this.props.print && <PrimaryButton onClick={this.handleAddRow}>Add Row</PrimaryButton>}
        <SimpleTable
          columns={columns}
          data={urinalysisTests}
        />
        <Modal isOpen={this.state.modalOpen}>
          <UrinalysisForm
            viewOnly={this.state.viewOnly}
            editRow={this.state.editRow}
            handleSaveRow={this.handleSaveRow}
            handleSaveNewRow={this.handleSaveNewRow}
            closeModal={this.closeModal}
          />
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  urinalysisTests: state.patient.data.testing.urinalysisTests,
  activeAccount: state.account.activeAccount,
  activePatientId: state.patient.data.activePatientId,
  print: state.patient.print,
});

const mapDispatchToProps = dispatch => ({
  addUrinalysisTest:
    (patientId, urinalysisTest) => dispatch(addUrinalysisTest(patientId, urinalysisTest)),
  deleteUrinalysisTest:
    (patientId, urinalysisTestId) => dispatch(deleteUrinalysisTest(patientId, urinalysisTestId)),
  updateUrinalysisTest:
    (patientId, urinalysisTest) => dispatch(updateUrinalysisTest(patientId, urinalysisTest)),
});

const UrinalysisTests = connect(
  mapStateToProps,
  mapDispatchToProps,
)(UrinalysisTestsComponent);

export default UrinalysisTests;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/testing/UrinalysisTests.jsx